/**
 * Thin wrappers around orval-generated Datasets hooks.
 *
 * Mirror the unwrap pattern from useModels — the generated hooks return
 * `{data, status, headers}` per orval v8 FETCH client. Mutations centralize
 * cache invalidation so dataset pages don't repeat queryKey plumbing.
 */

import { useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import type { ApiRequestError } from '@/api/client'
import {
  getGetDatasetApiDatasetsDatasetIdGetQueryKey,
  getListDatasetsApiDatasetsGetQueryKey,
  getListItemsApiDatasetsDatasetIdItemsGetQueryKey,
  useCreateDatasetApiDatasetsPost,
  useDeleteDatasetApiDatasetsDatasetIdDelete,
  useGetDatasetApiDatasetsDatasetIdGet,
  useListDatasetsApiDatasetsGet,
  useListItemsApiDatasetsDatasetIdItemsGet,
  useRemoveItemApiDatasetsDatasetIdItemsTraceIdDelete,
  useUpdateDatasetApiDatasetsDatasetIdPatch,
} from '@/api/generated/datasets/datasets'
import type {
  DatasetCreate,
  DatasetItemResponse,
  DatasetResponse,
  DatasetUpdate,
} from '@/api/generated/model'

export type Dataset = DatasetResponse
export type DatasetItem = DatasetItemResponse
export type { DatasetCreate, DatasetUpdate }

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

export function useDatasets(): UnwrappedQuery<Dataset[]> {
  const query = useListDatasetsApiDatasetsGet()
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useDataset(datasetId: string | undefined): UnwrappedQuery<Dataset> {
  const query = useGetDatasetApiDatasetsDatasetIdGet(datasetId ?? '', {
    query: { enabled: !!datasetId },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useDatasetItems(
  datasetId: string | undefined,
): UnwrappedQuery<DatasetItem[]> {
  const query = useListItemsApiDatasetsDatasetIdItemsGet(datasetId ?? '', {
    query: { enabled: !!datasetId },
  })
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

export function useCreateDataset(): UseMutationResult<
  unknown,
  ApiRequestError,
  { data: DatasetCreate }
> {
  const queryClient = useQueryClient()
  return useCreateDatasetApiDatasetsPost({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListDatasetsApiDatasetsGetQueryKey(),
        })
      },
    },
  })
}

export function useUpdateDataset(): UseMutationResult<
  unknown,
  ApiRequestError,
  { datasetId: string; data: DatasetUpdate }
> {
  const queryClient = useQueryClient()
  return useUpdateDatasetApiDatasetsDatasetIdPatch({
    mutation: {
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: getListDatasetsApiDatasetsGetQueryKey(),
        })
        void queryClient.invalidateQueries({
          queryKey: getGetDatasetApiDatasetsDatasetIdGetQueryKey(variables.datasetId),
        })
      },
    },
  })
}

export function useDeleteDataset(): UseMutationResult<
  unknown,
  ApiRequestError,
  { datasetId: string }
> {
  const queryClient = useQueryClient()
  return useDeleteDatasetApiDatasetsDatasetIdDelete({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getListDatasetsApiDatasetsGetQueryKey(),
        })
      },
    },
  })
}

export function useRemoveDatasetItem(): UseMutationResult<
  unknown,
  ApiRequestError,
  { datasetId: string; traceId: string }
> {
  const queryClient = useQueryClient()
  return useRemoveItemApiDatasetsDatasetIdItemsTraceIdDelete({
    mutation: {
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: getListDatasetsApiDatasetsGetQueryKey(),
        })
        void queryClient.invalidateQueries({
          queryKey: getGetDatasetApiDatasetsDatasetIdGetQueryKey(variables.datasetId),
        })
        void queryClient.invalidateQueries({
          queryKey: getListItemsApiDatasetsDatasetIdItemsGetQueryKey(variables.datasetId),
        })
      },
    },
  })
}
